import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CountUpNumber from "./CountUpNumber";

const StatusMetrics = ({ ticketStatusData, loading = false, type = 1 }) => {
  const navigate = useNavigate();

  // Navigation handlers for each card type
  const handleCardClick = (cardId, cardType) => {
    if (cardType === 1) {
      // Assigned cases type
      switch (cardId) {
        case 1:
          // Total Assigned Cases -> Service Request page, Assigned tab
          navigate("/serviceRequest", { state: { defaultTab: "1" } });
          break;
        case 2:
          // On Time Assigned Cases -> Service Request page, Assigned tab
          navigate("/serviceRequest", { state: { defaultTab: "1" } });
          break;
        case 3:
          // Not On Time Assigned Cases -> Case History page, UnAssigned tab
          navigate("/caseHistory", { state: { defaultTab: "1" } });
          break;
        default:
          break;
      }
    } else {
      // Completed cases type (type === 2)
      switch (cardId) {
        case 1:
          // Total Completed Cases -> Service Request page, Completed tab
          navigate("/serviceRequest", { state: { defaultTab: "3" } });
          break;
        case 2:
          // On Time Completed Cases -> Service Request page, Completed tab
          navigate("/serviceRequest", { state: { defaultTab: "3" } });
          break;
        case 3:
          // Not On Time Completed Cases -> Case History page, StaffConnect tab
          navigate("/caseHistory", { state: { defaultTab: "2" } });
          break;
        default:
          break;
      }
    }
  };

  const metricsData = [
    {
      id: 1,
      label: "Total Assigned Cases",
      value: ticketStatusData?.totalAssignedCases || 0,
      icon: AssignmentIcon,
      iconBgColor: "#3B82F6",
      valueColor: "#2563EB",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
    },
    {
      id: 2,
      label: "On Time Assigned Cases",
      value: ticketStatusData?.onTimeAssignedCases || 0,
      icon: CheckCircleIcon,
      iconBgColor: "#22C55E",
      valueColor: "#16A34A",
      gradient: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
    },
    {
      id: 3,
      label: "Not On Time Assigned Cases",
      value: ticketStatusData?.notOnTimeAssignedCases || 0,
      icon: ErrorIcon,
      iconBgColor: "#EF4444",
      valueColor: "#DC2626",
      gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
    },
  ];

  const metricsData_2 = [
    {
      id: 1,
      label: "Total Completed Cases",
      value:
        (ticketStatusData?.closed?.within || 0) +
        (ticketStatusData?.closed?.without || 0) || 0,
      icon: AssignmentIcon,
      iconBgColor: "#8162ffff",
      valueColor: "#603bf6ff",
      gradient: "linear-gradient(135deg, #8162ffff 0%, #603bf6ff 100%)",
    },
    {
      id: 2,
      label: "On Time Completed Cases",
      value: ticketStatusData?.closed?.within || 0,
      icon: CheckCircleIcon,
      iconBgColor: "#4cb781ff",
      valueColor: "#00FF7F",
      gradient: "linear-gradient(135deg, #4cb781ff 0%, #00FF7F 100%)",
    },
    {
      id: 3,
      label: "Not On Time Completed Cases",
      value: ticketStatusData?.closed?.without || 0,
      icon: ErrorIcon,
      iconBgColor: "#FF7F50",
      valueColor: "#ff5b1fff",
      gradient: "linear-gradient(135deg, #FF7F50 0%, #ff5b1fff 100%)",
    },
  ];
  if (loading) {
    return (
      <Stack
        spacing={3.5}
        className="translate-y-[-1rem] animate-fade-in opacity-0"
      >
        <Box>
          <Skeleton variant="text" width={220} height={28} />
        </Box>
        <Stack direction="row" spacing={3}>
          {[0, 1, 2].map((i) => (
            <Card
              key={i}
              sx={{
                flex: 1,
                minHeight: "90px",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "24px",
                }}
              >
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Skeleton
                    variant="rounded"
                    width={48}
                    height={48}
                    sx={{ borderRadius: 2 }}
                  />
                  <Stack spacing={2} sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" height={18} />
                    <Skeleton variant="text" width="30%" height={28} />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={3.5}
      className="translate-y-[-1rem] animate-fade-in opacity-0"
    >
      <Box>
        {/* <Typography variant="h6" sx={{ fontFamily: "Inter, Helvetica", fontWeight: 600, color: "#1F2937", fontSize: "18px" }}>
            Ticket Status Report
          </Typography> */}
      </Box>

      <Stack direction="row" spacing={3}>
        {[...(type === 1 ? metricsData : metricsData_2)].map(
          (metric, index) => (
            <Card
              key={metric.id}
              className="animate-fade-up opacity-0 interactiveCard"
              onClick={() => handleCardClick(metric.id, type)}
              sx={{
                flex: 1,
                minHeight: "120px",
                borderRadius: "16px",
                border: "none",
                boxShadow:
                  "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                "--animation-delay": `${(index + 1) * 200}ms`,
                position: "relative",
                overflow: "hidden",
                cursor: "pointer",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: metric.gradient,
                  transform: "scaleX(0)",
                  transformOrigin: "left",
                  transition: "transform 0.3s ease",
                },
                "&:hover": {
                  transform: "translateY(-8px) scale(1.02)",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  "&::before": {
                    transform: "scaleX(1)",
                  },
                  "& .iconBox": {
                    transform: "scale(1.1) rotate(5deg)",
                    boxShadow: `0 10px 20px -5px ${metric.iconBgColor}40`,
                  },
                },
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  padding: "28px",
                  "&:last-child": { paddingBottom: "28px" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={3}
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Box
                    className="iconBox"
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "16px",
                      background: metric.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: `0 4px 12px -2px ${metric.iconBgColor}50`,
                    }}
                  >
                    <metric.icon sx={{ color: "#FFFFFF", fontSize: 32 }} />
                  </Box>
                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "Inter, Helvetica",
                        fontWeight: 500,
                        color: "#6b7280",
                        fontSize: "14px",
                        lineHeight: "normal",
                        letterSpacing: "0.2px",
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Inter, Helvetica",
                        fontWeight: 700,
                        color: metric.valueColor,
                        fontSize: "32px",
                        lineHeight: "normal",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      <CountUpNumber value={metric.value} duration={2000} />
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )
        )}
      </Stack>
    </Stack>
  );
};

export default StatusMetrics;
